import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

// `window.history.length` counts every entry in the tab, including pages from
// other sites. A link opened from an SMS or email often lands in a tab that
// already has history, so it can't tell us whether there's a page of *ours* to
// go back to. Instead we remember the history entry the visitor landed on
// (Next.js gives every entry a unique `key`) and only offer "back" once they've
// moved away from it within the app.

type LandingEntry = { key: string | undefined; cameFromSameOrigin: boolean };

let landingEntry: LandingEntry | null = null;

const sameOriginReferrer = () => {
  try {
    return !!document.referrer && new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
};

/** Records the entry the visitor landed on. Call once on app start, before any navigation. */
export const recordLandingEntry = () => {
  if (landingEntry || typeof window === 'undefined') return;
  landingEntry = {
    key: window.history.state?.key,
    // A full page load from one of our own pages (not a new tab) still has us behind it.
    cameFromSameOrigin: sameOriginReferrer() && window.history.length > 1,
  };
};

export const canGoBackInApp = () => {
  if (typeof window === 'undefined') return false;
  recordLandingEntry();
  if (landingEntry?.cameFromSameOrigin) return true;
  const currentKey = window.history.state?.key;
  return !!currentKey && currentKey !== landingEntry?.key;
};

export function useCanGoBack() {
  const { asPath } = useRouter();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    setCanGoBack(canGoBackInApp());
  }, [asPath]);

  return canGoBack;
}
